/**
 * SmartTokenChip — what a `smart-token` ContentBlock renders as in the
 * master-spec editor canvas. Per the 2026-05-12 product decision:
 *
 *   "It shows what it's going to show because most of the parameters
 *    would come from the project, and it won't render that info until
 *    it's compiled into a project."
 *
 * So we render the DECLARATION: icon + name + a compact summary of which
 * params are set. NOT a mock-resolved preview against fake data. Authors
 * see what kind of block it IS, not what it will produce — they'll see
 * the produced content when the spec is compiled into a real project.
 *
 * Click → selects the block (right-rail opens the params panel).
 */

import React from 'react';
import * as LucideIcons from 'lucide-react';
import type { ContentBlock, SmartBlock } from '../../../shared/types';
import { findSmartBlockInCatalog, builtInIdFor } from '../../../engine/smart-blocks';

interface Props {
  block: ContentBlock;
  catalog: SmartBlock[];
  selected: boolean;
  onClick?: (e: React.MouseEvent) => void;
}

export function SmartTokenChip({ block, catalog, selected, onClick }: Props) {
  const sb =
    findSmartBlockInCatalog(block.smartBlockId, catalog) ??
    (block.smartBlockKind
      ? findSmartBlockInCatalog(builtInIdFor(block.smartBlockKind as any), catalog)
      : undefined);
  const name = sb?.name ?? block.smartBlockKind ?? 'Smart Block';
  const iconName = sb?.icon ?? 'Sparkles';
  const accent = sb?.color ?? '#228be6';
  const params = block.smartParams ?? sb?.defaultParams ?? {};
  const summary = buildParamSummary(params, sb);
  // @ts-ignore — dynamic lucide lookup
  const Icon = (LucideIcons[iconName] as any) ?? LucideIcons.Sparkles;
  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label={`Smart Block: ${name}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 12px 6px 10px',
        borderRadius: 8,
        border: `1px solid ${selected ? accent : 'var(--bd, var(--color-border))'}`,
        background: selected ? `${accent}14` : 'var(--sf, var(--color-surface))',
        boxShadow: selected ? `0 0 0 2px ${accent}33` : 'none',
        cursor: 'pointer',
        userSelect: 'none',
        transition: 'all 120ms ease',
        maxWidth: '100%',
        overflow: 'hidden',
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 20,
          height: 20,
          borderRadius: 4,
          background: `${accent}22`,
          color: accent,
          flexShrink: 0,
        }}
      >
        <Icon size={13} strokeWidth={2.5} />
      </span>
      <span
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--tx, var(--color-text))',
          letterSpacing: 0.1,
          whiteSpace: 'nowrap',
        }}
      >
        {name}
      </span>
      {summary && (
        <>
          <span
            style={{
              width: 1,
              height: 12,
              background: 'var(--bd, var(--color-border))',
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontSize: 11,
              color: 'var(--tm, var(--color-text-muted))',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
            }}
          >
            {summary}
          </span>
        </>
      )}
      <span
        style={{
          fontSize: 9,
          fontWeight: 700,
          color: accent,
          background: `${accent}22`,
          padding: '1px 5px',
          borderRadius: 3,
          letterSpacing: 0.6,
          textTransform: 'uppercase',
          flexShrink: 0,
        }}
      >
        Smart
      </span>
    </div>
  );
}

function buildParamSummary(params: Record<string, any>, sb: SmartBlock | undefined): string {
  // Hand-tuned per kind so the summary is informative, not noisy.
  if (!sb) return '';
  const k = sb.kind;
  switch (k) {
    case 'related-sections': {
      const div = params.divisionFilter;
      const fmt = params.format ?? 'csi-and-title';
      const excl = (params.excludeIds ?? []).length;
      const parts: string[] = [];
      if (div) parts.push(`Div ${div}`);
      if (excl) parts.push(`${excl} excluded`);
      if (fmt && fmt !== 'csi-and-title') parts.push(fmt);
      return parts.join(' · ');
    }
    case 'section-header':
      return params.format ?? '';
    case 'project-identity': {
      const fields = params.fields ?? [];
      return fields.length === 1 ? fields[0] : `${fields.length} fields`;
    }
    case 'issue-date':
      return params.format ?? 'long';
    case 'standards-referenced': {
      const orgs = params.organizations ?? [];
      return orgs.length === 1 ? orgs[0] : `${orgs.length} orgs`;
    }
    case 'engineer-of-record':
      return params.titleMatch ? `matches "${params.titleMatch}"` : '';
    case 'custom':
      return params._customLabel ?? '';
    default:
      return '';
  }
}
